import type { CourseColor } from "@/api/types";

interface CourseColorClasses {
  /** Solid swatch (dots, the color picker). */
  swatch: string;
  /** Soft tinted background + readable foreground (chips, icon tiles). */
  soft: string;
  /** Gradient stops for course cover bands: a light tint fading into the surface. */
  cover: string;
}

/**
 * Course accent colors. These are decorative identity colors for course chips and cards —
 * not data encodings. Pink, blue and green reuse the brand and status tints; the rest use
 * Tailwind's palette directly.
 */
export const COURSE_COLORS: Record<CourseColor, CourseColorClasses> = {
  pink: {
    swatch: "bg-brand",
    soft: "bg-accent text-brand-ink",
    cover: "from-brand/18 via-brand/6 to-transparent",
  },
  blue: {
    swatch: "bg-blue-500",
    soft: "bg-info-soft text-info",
    cover: "from-blue-500/18 via-blue-500/6 to-transparent",
  },
  teal: {
    swatch: "bg-teal-500",
    soft: "bg-teal-500/12 text-teal-800 dark:text-teal-300",
    cover: "from-teal-500/18 via-teal-500/6 to-transparent",
  },
  green: {
    swatch: "bg-green-600",
    soft: "bg-success-soft text-success",
    cover: "from-green-600/18 via-green-600/6 to-transparent",
  },
  amber: {
    swatch: "bg-amber-500",
    soft: "bg-amber-500/15 text-amber-800 dark:text-amber-300",
    cover: "from-amber-500/20 via-amber-500/6 to-transparent",
  },
  orange: {
    swatch: "bg-orange-500",
    soft: "bg-orange-500/12 text-orange-800 dark:text-orange-300",
    cover: "from-orange-500/18 via-orange-500/6 to-transparent",
  },
  rose: {
    swatch: "bg-rose-500",
    soft: "bg-rose-500/12 text-rose-800 dark:text-rose-300",
    cover: "from-rose-500/18 via-rose-500/6 to-transparent",
  },
  slate: {
    swatch: "bg-slate-500",
    soft: "bg-slate-500/12 text-slate-800 dark:text-slate-300",
    cover: "from-slate-500/18 via-slate-500/6 to-transparent",
  },
};

export const COURSE_COLOR_OPTIONS = Object.keys(COURSE_COLORS) as CourseColor[];

export function courseColor(color: CourseColor | undefined): CourseColorClasses {
  return COURSE_COLORS[color ?? "pink"] ?? COURSE_COLORS.pink;
}
